import type {
  Cell,
  CellData,
  Column,
  Header,
  Row,
  RowData,
} from '@tanstack/react-table';
import {
  type PropsWithChildren,
  useContext,
  createContext,
  type JSX,
} from 'react';

import type { TDataTableFeatures } from '../features/types/features.types.js';
import type { DataTableInstance } from '../features/types/table.types.js';
import type { DataTableColumnSettingsConfig } from '../public.api.js';

//#region table context
// TanStack Table 9 made `TData` invariant, so a `Row<_, TData>` no longer flows
// into a `Row<_, RowData>`. The contexts below are inherently heterogeneous —
// one context instance serves every table regardless of its row type — so the
// erasure to `RowData` happens once here, at the provider boundary.
const DataTableContext = createContext<DataTableInstance<RowData> | null>(null);

/** @internal */
export function DataTableContextProvider<TData extends RowData>(
  props: PropsWithChildren<{ value: DataTableInstance<TData> }>,
) {
  return (
    <DataTableContext.Provider
      value={props.value as DataTableInstance<RowData>}
    >
      {props.children}
    </DataTableContext.Provider>
  );
}

/** @internal */
export function useDataTable(): DataTableInstance<RowData> {
  const table = useContext<DataTableInstance<RowData> | null>(DataTableContext);

  // Typescript assertion function, as in the context, the table will
  // always be defined.
  if (table === null) {
    throw new Error('Table instance is not defined in context.');
  }

  return table;
}

//#endregion

//#region content context
/** @internal */
interface DataTableContentContextProps {
  hasOverflowLeft: boolean;
  hasOverflowRight: boolean;
  isHorizontallyScrollable: boolean;
}

const DEFAULT_CONTENT_CONTEXT = {
  hasOverflowLeft: false,
  hasOverflowRight: false,
  isHorizontallyScrollable: false,
};

export const DataTableContentContext =
  createContext<DataTableContentContextProps>(DEFAULT_CONTENT_CONTEXT);

/** @internal */
export function DataTableContentContextProvider(
  props: PropsWithChildren<{ value?: DataTableContentContextProps }>,
): JSX.Element {
  return (
    <DataTableContentContext.Provider
      value={props.value ?? DEFAULT_CONTENT_CONTEXT}
    >
      {props.children}
    </DataTableContentContext.Provider>
  );
}

/** @internal */
export function useDataTableContent(): DataTableContentContextProps {
  const content = useContext<DataTableContentContextProps>(
    DataTableContentContext,
  );

  // Typescript assertion function, as in the context, the table will
  // always be defined.
  if (content === null) {
    throw new Error('Content instance is not defined in context.');
  }

  return content;
}

//#endregion

//#region header context
/** @internal */
export const DataTableHeaderContext = createContext<Header<
  TDataTableFeatures,
  RowData,
  CellData
> | null>(null);

/** @internal */
export function DataTableHeaderContextProvider(
  props: PropsWithChildren<{
    value?: Header<TDataTableFeatures, RowData, CellData>;
  }>,
): JSX.Element {
  return (
    <DataTableHeaderContext.Provider value={props.value ?? null}>
      {props.children}
    </DataTableHeaderContext.Provider>
  );
}

/** @internal */
export function useDataTableHeader(): Header<
  TDataTableFeatures,
  RowData,
  CellData
> {
  const header = useContext<Header<
    TDataTableFeatures,
    RowData,
    CellData
  > | null>(DataTableHeaderContext);

  // Typescript assertion function, as in the context, the table will
  // always be defined.
  if (header === null) {
    throw new Error('Header instance is not defined in context.');
  }

  return header;
}

//#endregion

//#region column context
const DataTableColumnContext = createContext<Column<
  TDataTableFeatures,
  RowData,
  CellData
> | null>(null);

/** @internal */
export function DataTableColumnContextProvider(
  props: PropsWithChildren<{
    value?: Column<TDataTableFeatures, RowData, CellData>;
  }>,
): JSX.Element {
  return (
    <DataTableColumnContext.Provider value={props.value ?? null}>
      {props.children}
    </DataTableColumnContext.Provider>
  );
}

/** @internal */
export function useDataTableColumn(): Column<
  TDataTableFeatures,
  RowData,
  CellData
> {
  const column = useContext<Column<
    TDataTableFeatures,
    RowData,
    CellData
  > | null>(DataTableColumnContext);

  // Typescript assertion function, as in the context, the table will
  // always be defined.
  if (column === null) {
    throw new Error('Column object is not defined in context.');
  }

  return column;
}

//#endregion

//#region row context
const DataTableRowContext = createContext<Row<
  TDataTableFeatures,
  RowData
> | null>(null);

/** @internal */
export function DataTableRowContextProvider(
  props: PropsWithChildren<{
    value?: Row<TDataTableFeatures, RowData>;
  }>,
): JSX.Element {
  return (
    <DataTableRowContext.Provider value={props.value ?? null}>
      {props.children}
    </DataTableRowContext.Provider>
  );
}

/** @internal */
export function useDataTableRow(): Row<TDataTableFeatures, RowData> | null {
  const row = useContext<Row<TDataTableFeatures, RowData> | null>(
    DataTableRowContext,
  );

  return row;
}

//#endregion

//#region cell context
const DataTableCellContext = createContext<Cell<
  TDataTableFeatures,
  RowData,
  CellData
> | null>(null);

/** @internal */
export function DataTableCellContextProvider(
  props: PropsWithChildren<{
    value?: Cell<TDataTableFeatures, RowData, CellData>;
  }>,
): JSX.Element {
  return (
    <DataTableCellContext.Provider value={props.value ?? null}>
      {props.children}
    </DataTableCellContext.Provider>
  );
}

/** @internal */
export function useDataTableCell(): Cell<
  TDataTableFeatures,
  RowData,
  CellData
> | null {
  const cell = useContext<Cell<TDataTableFeatures, RowData, CellData> | null>(
    DataTableCellContext,
  );

  return cell;
}

//#endregion

//#region column settings context
/** @internal */
export type DataTableColumnSettingsHandle = {
  openColumnSettings?: (columnSettings?: DataTableColumnSettingsConfig) => void;
};

const DataTableColumnSettingsContext =
  createContext<DataTableColumnSettingsHandle | null>(null);

/** @internal */
export function DataTableColumnSettingsContextProvider(
  props: PropsWithChildren<{
    value?: DataTableColumnSettingsHandle;
  }>,
): JSX.Element {
  return (
    <DataTableColumnSettingsContext.Provider value={props.value ?? null}>
      {props.children}
    </DataTableColumnSettingsContext.Provider>
  );
}

/** @internal */
export function useDataTableColumnSettings(): DataTableColumnSettingsHandle | null {
  return useContext<DataTableColumnSettingsHandle | null>(
    DataTableColumnSettingsContext,
  );
}

//#endregion

//#region isHeader context
const DataTableIsHeaderContext = createContext(false);

/** @internal */
export function DataTableIsHeaderContextProvider(
  props: Readonly<PropsWithChildren>,
): JSX.Element {
  return (
    <DataTableIsHeaderContext.Provider value={true}>
      {props.children}
    </DataTableIsHeaderContext.Provider>
  );
}

/** @internal */
export function useDataTableIsHeaderContextProvider(): boolean {
  return useContext<boolean>(DataTableIsHeaderContext);
}
//#endregion
